import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck, Sparkles, BarChart3, Quote } from "lucide-react";
import CTAButtons from "@/components/CTAButtons";
import TrustBar from "@/components/TrustBar";
import { services } from "@/data/services";

const SHORT_LOGO =
    "/assets/tca-logo-black.png";

const STATS = [
    { num: "10+", label: "Years of practice" },
    { num: "350+", label: "Active clients" },
    { num: "9", label: "Service packages" },
    { num: "Gold", label: "Xero Partner status" },
];

export default function Home() {
    const navigate = useNavigate();
    return (
        <div data-testid="page-home">
            {/* Hero */}
            <section className="relative overflow-hidden bg-white border-b border-tca-line">
                <div className="absolute inset-0 grid-bg pointer-events-none opacity-70" />
                <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-tca-orangeSoft blur-3xl opacity-70 pointer-events-none" />
                <div className="container-tca relative pt-16 pb-24 md:pt-24 md:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
                    <div className="lg:col-span-8 animate-fade-up">
                        <div className="flex items-center gap-3 mb-7" data-testid="hero-badge">
                            <span className="inline-block w-8 h-px bg-tca-orange" />
                            <p className="overline text-tca-orange">Established 2015 · Chartered Accountants &amp; Auditors</p>
                        </div>
                        <h1 className="font-display font-extrabold text-tca-ink text-[44px] sm:text-6xl lg:text-7xl xl:text-[88px] leading-[0.96] tracking-[-0.025em]">
                            Our ambition is to deliver the
                            <span className="text-tca-orange"> highest quality </span>
                            client service.
                        </h1>
                        <p className="mt-7 text-base sm:text-lg text-tca-mute max-w-2xl leading-relaxed">
                            TC Auditors is a registered audit and accounting practice based in Meyerton, Gauteng. For over a decade we have helped South African owners stay compliant, profitable and properly audited — powered by Xero and a team that genuinely cares.
                        </p>
                        <CTAButtons className="mt-10" testIdPrefix="hero-cta" />
                    </div>
                    <div className="lg:col-span-4 hidden lg:block">
                        <div className="relative border border-tca-line bg-white p-8 lift">
                            <Quote className="w-8 h-8 text-tca-orange mb-4" />
                            <p className="text-tca-ink text-lg leading-relaxed font-display font-semibold">
                                “Auditing is not about finding fault — it is about giving the directors the confidence to keep building.”
                            </p>
                            <div className="mt-6 pt-6 border-t border-tca-line flex items-center gap-3">
                                <img src={SHORT_LOGO} alt="TCA" className="h-10 w-10 object-contain" />
                                <div>
                                    <p className="text-sm font-bold">Schalk Coetzee CA(SA), RA</p>
                                    <p className="text-xs text-tca-mute">Director, TC Auditors</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Trust bar */}
            <TrustBar />

            {/* Stats / Why TC */}
            <section className="bg-white">
                <div className="container-tca py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <p className="overline mb-4 text-tca-orange">Why TC Auditors</p>
                        <h2 className="font-display font-extrabold text-tca-ink text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
                            A modern audit firm with old-fashioned attention to detail.
                        </h2>
                        <p className="mt-6 text-tca-mute leading-relaxed">
                            We are Chartered Accountants and Registered Auditors who run our own books on Xero. That means every recommendation we make is one we have already lived through — from VAT verifications to Audit File reviews.
                        </p>
                        <Link to="/about" data-testid="why-cta" className="btn-ghost mt-8">
                            Read our story <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <div className="lg:col-span-7 grid grid-cols-2 gap-px bg-tca-line border border-tca-line">
                        {STATS.map((s) => (
                            <div
                                key={s.label}
                                className="bg-white p-8 md:p-10"
                                data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}
                            >
                                <p className="font-display font-extrabold text-5xl md:text-6xl text-tca-ink tracking-tight">
                                    {s.num}
                                </p>
                                <p className="mt-2 text-sm text-tca-mute">{s.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services preview */}
            <section className="bg-tca-fog border-y border-tca-line">
                <div className="container-tca py-20 md:py-28">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
                        <div className="max-w-2xl">
                            <p className="overline mb-3">Our Services</p>
                            <h2 className="font-display font-extrabold text-tca-ink text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
                                Every compliance need under one roof.
                            </h2>
                        </div>
                        <Link to="/services" data-testid="services-view-all" className="btn-ghost">
                            View all services <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-tca-line border border-tca-line">
                        {services.slice(0, 6).map((s, i) => {
                            const Icon = s.icon;
                            const isAlt = i % 2 !== 0;
                            return (
                                <div
                                    key={s.slug}
                                    data-testid={`service-card-${s.slug}`}
                                    onClick={s.slug === "personal-tax" ? () => navigate("/pricing", { state: { openView: "personal" } }) : s.slug === "accounting" ? () => navigate("/pricing", { state: { openView: "company" } }) : undefined}
                                    className={`p-7 lg:p-9 group lift transition-colors duration-200 ${
                                        isAlt ? "bg-orange-100" : "bg-white"
                                    } hover:bg-tca-orange${s.slug === "personal-tax" || s.slug === "accounting" ? " cursor-pointer" : ""}`}
                                >
                                    <div className="flex items-center justify-between mb-5">
                                        <div className="w-11 h-11 bg-tca-orangeSoft group-hover:bg-white/20 text-tca-orange group-hover:text-white flex items-center justify-center transition-colors duration-200">
                                            <Icon className="w-5 h-5" />
                                        </div>
                                        <span className="text-tca-line group-hover:text-white/30 text-sm tabular-nums transition-colors duration-200">0{i + 1}</span>
                                    </div>
                                    <h3 className="font-display font-bold text-xl text-tca-ink group-hover:text-white mb-3 transition-colors duration-200">
                                        {s.title}
                                    </h3>
                                    <p className="text-sm text-tca-mute group-hover:text-white/80 leading-relaxed transition-colors duration-200">{s.summary}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Approach 3-up */}
            <section className="bg-white">
                <div className="container-tca py-20 md:py-28 grid grid-cols-1 md:grid-cols-3 gap-px bg-tca-line border border-tca-line">
                    {[
                        {
                            icon: ShieldCheck,
                            title: "Regulated assurance",
                            text: "Audits and Reviews signed by an active Registered Auditor (IRBA), in line with International Standards on Auditing.",
                        },
                        {
                            icon: BarChart3,
                            title: "Live management accounts",
                            text: "We keep your books in Xero so you and the SARB never wait for end-of-year. Quarterly to monthly cadence.",
                        },
                        {
                            icon: Sparkles,
                            title: "AI-driven automations",
                            text: "Zapier, ChatGPT and the Xero App Store integrate your invoicing, banking and CRM. Less admin, more margin.",
                        },
                    ].map((b) => {
                        const Icon = b.icon;
                        return (
                            <div key={b.title} className="bg-white p-9 lg:p-12">
                                <Icon className="w-7 h-7 text-tca-orange mb-6" />
                                <h3 className="font-display font-bold text-2xl text-tca-ink mb-3">{b.title}</h3>
                                <p className="text-tca-mute leading-relaxed">{b.text}</p>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Big CTA */}
            <section className="bg-tca-ink text-white relative overflow-hidden">
                <div className="absolute -top-20 -left-20 w-[500px] h-[500px] rounded-full bg-tca-orange opacity-20 blur-3xl" />
                <div className="container-tca py-20 md:py-28 relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-8">
                        <p className="overline text-tca-orange mb-4">Let's talk</p>
                        <h2 className="font-display font-extrabold text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.05] max-w-3xl">
                            Whether it is a quick question or a full audit — we are ten years into doing this well.
                        </h2>
                    </div>
                    <div className="lg:col-span-4 flex flex-col gap-3">
                        <CTAButtons testIdPrefix="footer-cta" inquiryLabel="Send a General Inquiry" scheduleLabel="Book a 30-min Virtual Meeting" />
                    </div>
                </div>
            </section>
        </div>
    );
}
